import { el } from "./dom.js";
import { api } from "../net/api.js";
import { installKind, onInstallChange, promptInstall } from "../pwa.js";

export interface MenuActions {
  onLogout: () => void;
  onPractice: () => void;
  onTutorial: () => void;
  onPlay: () => void;
  onCosmetics: () => void;
  onMapEditor: () => void;
  onSettings: () => void;
  onProfile: () => void;
  onLeaderboard: () => void;
  onFriends: () => void;
  onAdmin: () => void;
}

/** Main menu after login. Multiplayer entries land in later milestones. */
export function menuScreen(actions: MenuActions): HTMLElement {
  const screen = el("div", { class: "screen menu-hero" });
  const panel = el("div", { class: "panel menu-panel" });

  const logout = el("button", { class: "ghost wide", text: "Log Out" });
  logout.onclick = async () => {
    await api.logout();
    actions.onLogout();
  };

  const practice = el("button", { class: "wide mt1", text: "Practice Range 🏁", title: "Free practice on live targets — first kill starts the LADDER SPRINT clock; beat your best full-ladder time" });
  practice.onclick = actions.onPractice;
  const tutorial = el("button", { class: "wide", text: "🎓 Training (tutorial)" });
  tutorial.onclick = actions.onTutorial;

  const play = el("button", { class: "primary wide", text: "Play", "data-pad-start": "" });
  play.onclick = actions.onPlay;

  const cosmetics = el("button", { class: "magenta wide mt1", text: "Cosmetics" });
  cosmetics.onclick = actions.onCosmetics;

  const mapEditor = el("button", { class: "wide", text: "🛠 Build a Map" });
  mapEditor.onclick = actions.onMapEditor;

  const settings = el("button", { class: "ghost wide mt1", text: "Settings" });
  settings.onclick = actions.onSettings;

  // "Install the app" — visible whenever the browser can add NEON PURGE to
  // the home screen / desktop (hidden once installed or already standalone)
  const installBtn = el("button", { class: "wide mt1", text: "📲 Install the app", style: "display:none;border-color:#9dff00;color:#9dff00" });
  installBtn.onclick = () => promptInstall();
  const syncInstall = (): void => { installBtn.style.display = installKind() ? "" : "none"; };
  // anchored to the button: menuScreen is rebuilt on every return to the menu
  // (after every match and every sub-screen Back), and an unanchored callback
  // is never pruned — emit() only drops entries whose anchor left the DOM.
  onInstallChange(syncInstall, installBtn);
  syncInstall();

  const profile = el("button", { class: "ghost", text: "Profile", style: "flex:1" });
  profile.onclick = actions.onProfile;
  const leaderboard = el("button", { class: "ghost", text: "Leaderboard", style: "flex:1" });
  leaderboard.onclick = actions.onLeaderboard;
  const friendsBtn = el("button", { class: "ghost", text: "Friends", style: "flex:1" });
  friendsBtn.onclick = actions.onFriends;
  // owner-only: the connection log panel
  const adminBtn = el("button", { class: "ghost", text: "👑 Admin", style: "flex:1" });
  adminBtn.onclick = actions.onAdmin;
  const isAdmin = (api.account?.username ?? "").toLowerCase() === "supremechibre";

  if (api.guest) logout.textContent = "Log In / Register";
  panel.append(
    el("div", { class: "brand", text: "NEON PURGE" }),
    el("div", { class: "brand-sub", text: "GUN GAME" }),
    el("div", { class: "hint", style: "opacity:0.5;font-size:0.72em", text: `build ${__BUILD_STAMP__}` }),
    el("div", { class: "hint pc-hint", style: "color:#9dff00;opacity:0.85", text: "🎮 best experienced with a controller" }),
    (() => {
      const line = el("div", {
        class: "hint",
        text: api.guest
          ? `playing as ${api.account?.username ?? "guest"} — nothing is saved`
          : `logged in as ${api.account?.username ?? "?"}`,
      });
      // PROGRESSION: fetch the rank badge and append it once it lands. Guests
      // have no persisted XP, so don't even ask.
      if (!api.guest) {
        void api.getStatsAndRank().then(({ rank }) => {
          if (!rank) return;
          const badge = el("span", { class: "rank-chip", text: `${rank.name} ${rank.level}` });
          badge.style.color = rank.color;
          badge.style.borderColor = rank.color;
          line.append(" ", badge);
        }).catch(() => { /* menu must render even if stats are down */ });
      }
      return line;
    })(),
    play,
    practice,
    tutorial,
    cosmetics,
    mapEditor,
    // guests have no persistent stats — profile is meaningless for them
    el("div", { class: "row mt1" }, ...(api.guest ? [leaderboard] : [profile, leaderboard, friendsBtn]), ...(isAdmin ? [adminBtn] : [])),
    settings,
    installBtn,
    logout,
  );

  screen.append(panel);
  return screen;
}
